// =============================================================================
// MeuDriver — Batch P2: Hooks de códigos promocionais
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PromoCode {
  id: string
  code: string
  discount_type: 'percent' | 'fixed'
  discount_value: number
  max_uses: number | null
  current_uses: number
  valid_from: string
  valid_until: string | null
  is_active: boolean
  created_by: string | null
  created_at: string
}

export interface CreatePromoCodeInput {
  code: string
  discount_type: 'percent' | 'fixed'
  discount_value: number
  max_uses?: number | null
  valid_from?: string
  valid_until?: string | null
  is_active?: boolean
}

export interface UpdatePromoCodeInput extends Partial<CreatePromoCodeInput> {
  id: string
}

export interface ApplyPromoResult {
  discount_applied: number
  new_total: number
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const promoKeys = {
  all:   ['promo_codes'] as const,
  list:  () => [...promoKeys.all, 'list'] as const,
}

// ---------------------------------------------------------------------------
// useApplyPromoCode — aluno aplica um código num booking
// ---------------------------------------------------------------------------

export function useApplyPromoCode() {
  const qc = useQueryClient()

  return useMutation<ApplyPromoResult, Error, { code: string; bookingId: string }>({
    mutationFn: async ({ code, bookingId }) => {
      const { data, error } = await supabase.rpc('apply_promo_code', {
        p_code:       code,
        p_booking_id: bookingId,
      })
      if (error) throw new Error(error.message)
      // RPC retorna array de rows (RETURNS TABLE)
      const rows = data as { discount_applied: number; new_total: number }[] | null
      if (!rows || rows.length === 0) throw new Error('Resposta inesperada do servidor')
      return rows[0]
    },
    onSuccess: (result) => {
      toast.success(
        `Desconto de R$ ${result.discount_applied.toFixed(2)} aplicado com sucesso!`,
      )
      qc.invalidateQueries({ queryKey: ['bookings'] })
    },
    onError: (err) => {
      const msg = err.message.includes('inválido')
        ? 'Código inválido ou inativo.'
        : err.message.includes('expirado')
          ? 'Este código já expirou.'
          : err.message.includes('limite')
            ? 'Código atingiu o limite de usos.'
            : err.message.includes('já possui')
              ? 'Esta reserva já possui um desconto aplicado.'
              : 'Não foi possível aplicar o código. Tente novamente.'
      toast.error(msg)
    },
  })
}

// ---------------------------------------------------------------------------
// useAdminPromoCodes — lista todos os códigos (admin)
// ---------------------------------------------------------------------------

export function useAdminPromoCodes() {
  return useQuery<PromoCode[], Error>({
    queryKey: promoKeys.list(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('promo_codes')
        .select('*')
        .order('created_at', { ascending: false })
      if (error) throw new Error(error.message)
      return (data ?? []) as PromoCode[]
    },
  })
}

// ---------------------------------------------------------------------------
// useCreatePromoCode — admin cria novo código
// ---------------------------------------------------------------------------

export function useCreatePromoCode() {
  const qc = useQueryClient()

  return useMutation<void, Error, CreatePromoCodeInput>({
    mutationFn: async (input) => {
      const { error } = await supabase.from('promo_codes').insert({
        code:           input.code.toUpperCase().trim(),
        discount_type:  input.discount_type,
        discount_value: input.discount_value,
        max_uses:       input.max_uses ?? null,
        valid_from:     input.valid_from ?? new Date().toISOString(),
        valid_until:    input.valid_until ?? null,
        is_active:      input.is_active ?? true,
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      toast.success('Código criado com sucesso!')
      qc.invalidateQueries({ queryKey: promoKeys.list() })
    },
    onError: (err) => {
      const msg = err.message.includes('unique')
        ? 'Já existe um código com este nome.'
        : 'Erro ao criar código. Tente novamente.'
      toast.error(msg)
    },
  })
}

// ---------------------------------------------------------------------------
// useUpdatePromoCode — admin atualiza código existente
// ---------------------------------------------------------------------------

export function useUpdatePromoCode() {
  const qc = useQueryClient()

  return useMutation<void, Error, UpdatePromoCodeInput>({
    mutationFn: async ({ id, ...fields }) => {
      const { error } = await supabase
        .from('promo_codes')
        .update({
          ...fields,
          ...(fields.code ? { code: fields.code.toUpperCase().trim() } : {}),
        })
        .eq('id', id)
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      toast.success('Código atualizado com sucesso!')
      qc.invalidateQueries({ queryKey: promoKeys.list() })
    },
    onError: () => toast.error('Erro ao atualizar código.'),
  })
}
